<template>
  <div :id="modalId" class="modal overflow-y-auto flex" tabindex="-1" aria-hidden="true" :class="{ show: isOpen }">
    <div class="modal-dialog">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header px-5 py-3 border-b border-gray-200 dark:border-dark-5">
          <h2 class="font-medium text-base">
            {{ $t(`${viewRoot}.text11`) }}
          </h2>
        </div>

        <!-- Modal Body -->
        <div class="modal-body px-5 py-6">
          <!-- Name Input -->
          <div>
            <label :for="'quick-disease-name'" class="form-label font-semibold">
              {{ $t(`${viewRoot}.text12`) }}
            </label>
            <div class="input-group">
              <input
                :id="'quick-disease-name'"
                type="text"
                class="form-control w-full bg-theme-32"
                :class="validationError && !disease.name.trim() ? 'border border-theme-6' : ''"
                :placeholder="$t(`${viewRoot}.text12`)"
                v-model="disease.name"
                @input="clearValidationError"
                maxlength="110"
              />
            </div>
          </div>

          <!-- Description Input -->
          <div class="mt-5">
            <label :for="'quick-disease-desc'" class="form-label font-semibold">
              {{ $t(`${viewRoot}.text5`) }}
            </label>
            <div class="input-group">
              <textarea
                :id="'quick-disease-desc'"
                rows="4"
                class="form-control w-full bg-theme-32"
                :placeholder="$t(`${viewRoot}.text5`)"
                v-model="disease.description"
                maxlength="2000"
              ></textarea>
            </div>
          </div>

          <!-- Error messages -->
          <div v-if="validationError" class="text-theme-6 mt-3 text-sm font-semibold">
            {{ validationError }}
          </div>
          <div v-if="apiError" class="text-theme-6 mt-3 text-sm font-semibold">
            {{ apiError }}
          </div>
        </div>

        <!-- Modal Footer -->
        <div
          class="modal-footer px-5 py-3 border-t border-gray-200 dark:border-dark-5 text-right flex justify-end gap-3"
        >
          <button type="button" @click="close" class="btn btn-outline-secondary w-24 mr-2" :disabled="loading">
            {{ $t("components.cancel") }}
          </button>
          <button
            type="button"
            @click="save"
            class="btn btn-primary w-24 flex items-center justify-center"
            :disabled="loading"
          >
            <LoadingIcon v-if="loading" icon="three-dots" class="w-4 h-4 ml-2" />
            <span v-else>{{ $t("views.save") }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import DiseaseService from "@/services/diseases.service";
import Toastify from "toastify-js";

export default defineComponent({
  name: "DiseaseQuickCreateModal",
  props: {
    modalId: {
      type: String,
      default: "disease-quick-create-modal",
    },
  },
  emits: ["created"],
  data() {
    return {
      viewRoot: "views.disease",
      isOpen: false,
      loading: false,
      disease: {
        name: "",
        description: "",
      },
      validationError: "",
      apiError: "",
      lang_key: localStorage.lang ? localStorage.lang : navigator.language.split("-")[0],
    };
  },
  created() {
    this.resetForm();
  },
  methods: {
    resetForm() {
      this.disease = {
        name: "",
        description: "",
      };
      this.validationError = "";
      this.apiError = "";
    },
    show() {
      this.resetForm();
      this.isOpen = true;
      cash("#" + this.modalId).modal("show");
    },
    close() {
      this.isOpen = false;
      cash("#" + this.modalId).modal("hide");
    },
    clearValidationError() {
      this.validationError = "";
    },
    async save() {
      this.validationError = "";
      this.apiError = "";

      if (!this.disease.name.trim()) {
        this.validationError = this.$t("views.disease.text13");
        return;
      }

      this.loading = true;

      try {
        const serverResponse = await DiseaseService.save(this.disease, this.lang_key);

        const newDisease = { ...this.disease };
        if (serverResponse && serverResponse.id) {
          newDisease.id = serverResponse.id;
        }

        // Emit event to parent with the newly created disease details
        this.$emit("created", newDisease);

        this.showToast();
        this.close();
      } catch (error) {
        console.error("Error creating disease via quick modal:", error);
        this.apiError = error?.response?.data?.message || error?.message || "Error al guardar la enfermedad.";
      } finally {
        this.loading = false;
      }
    },
    showToast() {
      const id = "save-disease-success";
      const node = cash(`#${id}`).clone().removeClass("hidden")[0];
      if (node) {
        Toastify({
          node: node,
          duration: 3000,
          gravity: "top",
          position: "right",
          close: true,
        }).showToast();
      } else {
        const customNode = cash(`
          <div class="toastify-content flex">
            <div class="text-theme-9">✓</div>
            <div class="ml-4 mr-4">
              <div class="font-medium">${this.$t("views.disease.text9") || "Guardado con éxito."}</div>
            </div>
          </div>
        `)[0];
        Toastify({
          node: customNode,
          duration: 3000,
          gravity: "top",
          position: "right",
          close: true,
        }).showToast();
      }
    },
  },
});
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
